import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Avatar from './Avatar';
import { getImageUrl } from '../utils/imageUrl';

export interface PipelineProfile {
  kind: 'church_planter' | 'portal_candidate';
  id: number;
  name: string;
  churchName: string | null;
  location: string | null;
  headshotImagePath: string | null;
  hasPlanterAppUserLinked?: boolean;
}

const CLOUD_VISIBLE = 10;

interface PipelineProfileCloudProps {
  profiles: PipelineProfile[];
  title: string;
  listHref: string;
  accentClassName?: string;
}

const PipelineProfileCloud: React.FC<PipelineProfileCloudProps> = ({
  profiles,
  title,
  listHref,
  accentClassName = 'border-gray-200 bg-gray-50/80',
}) => {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const visible = profiles.slice(0, CLOUD_VISIBLE);
  const extra = Math.max(0, profiles.length - CLOUD_VISIBLE);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  if (profiles.length === 0) {
    return (
      <div
        className={`rounded-lg border border-dashed px-3 py-4 text-center text-xs text-gray-500 ${accentClassName}`}
      >
        No people in this bucket yet.
      </div>
    );
  }

  const modal =
    open &&
    createPortal(
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6">
        <button
          type="button"
          className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
          aria-label="Close"
          onClick={close}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative z-[101] w-full max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col rounded-xl bg-white shadow-xl border border-gray-200"
        >
          <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100">
            <div>
              <h3 id={titleId} className="text-base font-semibold text-gray-900">
                {title}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{profiles.length} in this bucket</p>
            </div>
            <button
              type="button"
              onClick={close}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <ul className="overflow-y-auto px-3 py-3 space-y-2">
            {profiles.map((p) => {
              const to = p.kind === 'church_planter' ? `/planters/${p.id}` : `/onboarding/${p.id}`;
              const img = getImageUrl(p.headshotImagePath, { width: 96, height: 96, quality: 80 });
              return (
                <li key={`${p.kind}-${p.id}`}>
                  <Link
                    to={to}
                    onClick={close}
                    className="flex gap-3 rounded-lg border border-transparent px-2 py-2 hover:bg-gray-50 hover:border-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {img ? (
                      <img
                        src={img}
                        alt=""
                        className="h-12 w-12 rounded-full object-cover flex-shrink-0 border border-gray-200"
                      />
                    ) : (
                      <Avatar name={p.name} size="md" className="flex-shrink-0" />
                    )}
                    <div className="min-w-0 text-left">
                      <div className="font-medium text-gray-900 truncate">{p.name}</div>
                      {p.churchName ? (
                        <div className="text-sm text-gray-600 truncate">{p.churchName}</div>
                      ) : null}
                      {p.location ? (
                        <div className="text-xs text-gray-500 truncate">{p.location}</div>
                      ) : null}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap gap-2 justify-end">
            <Link
              to={listHref}
              onClick={close}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Open full list
            </Link>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        className={`w-full rounded-lg border text-left transition hover:brightness-[1.02] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${accentClassName}`}
      >
        <div className="px-2 pt-2 pb-1">
          <div className="flex flex-wrap items-center justify-center gap-0 pl-1">
            {visible.map((p, i) => {
              const img = getImageUrl(p.headshotImagePath, { width: 64, height: 64, quality: 75 });
              const shift = i === 0 ? '' : '-ml-2.5';
              return (
                <div
                  key={`${p.kind}-${p.id}`}
                  className={`relative ${shift}`}
                  style={{ zIndex: visible.length - i }}
                >
                  {img ? (
                    <img
                      src={img}
                      alt=""
                      className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                  ) : (
                    <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border-2 border-white shadow-sm overflow-hidden bg-gray-200 flex items-center justify-center text-[10px] font-medium text-gray-600">
                      {p.name
                        .split(/\s+/)
                        .map((w) => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                  )}
                </div>
              );
            })}
            {extra > 0 ? (
              <div
                className="-ml-2.5 h-9 w-9 sm:h-10 sm:w-10 rounded-full border-2 border-white bg-gray-700 text-white text-xs font-semibold flex items-center justify-center shadow-sm"
                style={{ zIndex: 0 }}
              >
                +{extra}
              </div>
            ) : null}
          </div>
        </div>
        <div className="px-3 pb-2 pt-0.5 text-center">
          <span className="text-xs font-medium text-gray-600">Tap to expand</span>
        </div>
      </button>
      {modal}
    </>
  );
};

export default PipelineProfileCloud;
